import { Avatar } from '@mantine/core'

import { ApplicantStage } from 'src/graphql/types/applicants'
import { getInitials } from 'src/lib/formatters'

/**
 * Per-employee mini-funnel card for the Performance tab. Stacks
 * avatar + name + role at the top, then a vertical list of stage rows.
 *
 * Each row uses a TWO-LINE layout:
 *   Line 1: full stage label + count (justify-between, can't truncate)
 *   Line 2: full-width bar
 *
 * Bar widths are normalised against `globalMaxCount` (the largest stage
 * count across ALL employees in the grid) so cards are visually
 * comparable side-by-side — not just the funnel shape within one card.
 *
 * Pure presentational component — no data fetching, no Apollo. Container
 * (`EmployeeFunnelsContainer`) builds segments + globalMaxCount and feeds them in.
 */
export interface EmployeeFunnelCardSegment {
  id: string
  stage: ApplicantStage
  count: number
  color: string
  label: string
}

export interface EmployeeFunnelCardProps {
  name: string
  jobTitle: string | null
  coverImage: string | null
  total: number
  segments: ReadonlyArray<EmployeeFunnelCardSegment>
  /**
   * Largest stage count across the whole grid — used to normalise this
   * card's bars so widths are comparable card-to-card. When 0 (no data
   * anywhere), all bars render as empty rails.
   */
  globalMaxCount: number
  /**
   * Optional click handlers for navigating to a filtered Applicants slice.
   * Left optional so Storybook can render a static, non-navigable preview.
   * `onTotalClick` covers both the header avatar/name region and the total
   * number — clicking the card identity opens "everything this employee
   * touched in the period". `onSegmentClick(stage)` opens the slice for
   * that one stage. Segments with `count === 0` stay non-clickable.
   */
  onTotalClick?: () => void
  onSegmentClick?: (stage: ApplicantStage) => void
}

const EmployeeFunnelCard = ({
  name,
  jobTitle,
  coverImage,
  total,
  segments,
  globalMaxCount,
  onTotalClick,
  onSegmentClick,
}: EmployeeFunnelCardProps) => {
  const avatarSrc =
    coverImage && coverImage.length > 0 ? coverImage : undefined

  return (
    <div
      className="flex flex-col gap-3 rounded-md border border-doubleNickel-gray-200 bg-white p-3.5"
      data-testid="employee-funnel-card"
    >
      <div
        className={
          onTotalClick
            ? 'flex cursor-pointer items-center gap-2.5 hover:underline'
            : 'flex items-center gap-2.5'
        }
        onClick={onTotalClick}
        data-testid="employee-funnel-card-header"
      >
        <Avatar
          size="md"
          radius="xl"
          src={avatarSrc}
          alt={name}
          data-testid="employee-funnel-card-avatar"
        >
          {getInitials(name)}
        </Avatar>
        <div className="min-w-0 flex-1">
          <div
            className="truncate text-[13px] font-semibold text-gray-900"
            title={name}
          >
            {name}
          </div>
          {jobTitle && (
            <div
              className="truncate text-[10px] text-gray-500"
              title={jobTitle}
              data-testid="employee-funnel-card-job-title"
            >
              {jobTitle}
            </div>
          )}
        </div>
        <div
          className="text-[11px] tabular-nums text-gray-500"
          aria-label={`Total ${total}`}
        >
          {total.toLocaleString()}
        </div>
      </div>

      <div
        className="flex flex-col gap-2"
        data-testid="employee-funnel-card-funnel"
      >
        {segments.map((segment) => {
          const widthPct =
            globalMaxCount > 0 ? (segment.count / globalMaxCount) * 100 : 0
          // Empty buckets stay non-clickable — landing on an empty
          // Applicants list from a "0" bar is just confusing.
          const segmentClickable =
            Boolean(onSegmentClick) && segment.count > 0
          return (
            <div
              key={segment.id}
              className={
                segmentClickable
                  ? 'flex cursor-pointer flex-col gap-1 hover:underline'
                  : 'flex flex-col gap-1'
              }
              onClick={
                segmentClickable
                  ? () => onSegmentClick?.(segment.stage)
                  : undefined
              }
              data-testid={`employee-funnel-card-row-${segment.stage}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
                  {segment.label}
                </span>
                <span className="text-[11px] tabular-nums text-gray-700">
                  {segment.count.toLocaleString()}
                </span>
              </div>
              <div
                className="h-[8px] w-full overflow-hidden rounded-sm bg-gray-100"
                role="progressbar"
                aria-label={`${segment.label}: ${segment.count}`}
                aria-valuenow={segment.count}
                aria-valuemin={0}
                aria-valuemax={globalMaxCount}
              >
                <div
                  className="h-full rounded-sm transition-[width]"
                  data-testid={`employee-funnel-card-bar-${segment.stage}`}
                  style={{
                    width: `${widthPct}%`,
                    backgroundColor: segment.color,
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default EmployeeFunnelCard
